import React, { Fragment, useState, useEffect } from 'react'
import { Row, Col, Form, Accordion, Button, Card, Badge, OverlayTrigger, Tooltip } from 'react-bootstrap'
import Select from 'react-select'
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers'
import MomentUtils from '@date-io/moment'
import { format, startOfMonth } from 'date-fns'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons'

import PageLoading from '../../../components/Common/PageLoading'
import SystemConstants from '../../../constants/system.constants'
import accountService from '../../../services/account.service'
import dashboardService from '../../../services/dashboard.service'
import colaboradoresService from '../../../services/colaboradores.service'
import perfilService from '../../../services/perfil.service'
import { formatMoney } from '../../../utils/string.utils'
import { filtrarColaboradoresPorPerfisPermitidos } from '../../../utils/colaborador.utils'
import { status } from '../../../constants/common.constants'

const ESPECIALIDADE_PADRAO = 'CLÍNICO GERAL'
const LIMITE_CANCELAMENTO = 25
const VALOR_EFETIVACAO_ORTODONTIA = 10

const CARD_HELPS = {
  contratosEfetivados:
    'Quantidade de contratos formalizados no período na especialidade filtrada. Em Ortodontia, o totalizador geral mostra a quantidade bruta; no detalhe do colaborador, abate os cancelamentos do período.',
  procedimentos: 'Quantidade de procedimentos clínicos finalizados no período (produção).',
  valorEfetivacao:
    'Soma dos valores dos contratos formalizados no período. Em Ortodontia, cada contrato vale R$ 10,00; cancelamentos do período descontam R$ 10,00 cada.',
  producao: 'Soma dos valores dos procedimentos clínicos finalizados no período.',
  contratosComEntrada: 'Quantidade de contratos formalizados em que a 1ª parcela está paga.',
  valorEntrada:
    'Soma dos valores da 1ª parcela paga. Para Admin, mostra separadamente o % sobre contratos formalizados e o % extra de entrada (não soma os dois).',
  cancelamentos: 'Quantidade de planos cancelados cuja data de cancelamento está no período (somente Ortodontia nesta dashboard).',
  valorCancelamento:
    'Em Ortodontia: cancelamentos × R$ 10,00. Laranja em 25% e vermelho acima de 25%. Plano clínico não exibe taxa de cancelamento nesta dashboard.',
  comissaoProducao: 'Valor monetário calculado sobre a produção (produção × % de produção). Não é percentual.',
  comissaoEfetivacao:
    'Valor monetário calculado sobre os contratos formalizados. Em plano clínico, o % depende da faixa do total formalizado (até 200 mil / acima de 250 mil / acima de 300 mil); com entrada paga, soma o % extra de entrada. Em Ortodontia, R$ 10,00 por contrato líquido (já com desconto de cancelamentos).',
  comissaoTotal:
    'Soma dos valores de produção e de contratos formalizados. Clínico: sem desconto por cancelamento. Ortodontia: mostra o desconto de R$ 10,00 por cancelamento no período.',
}

const normalizarTexto = (texto) =>
  (texto || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .trim()

const formatPercent = (value) =>
  `${Number(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`

const formatFiltroData = (data) => {
  if (!data) return '—'
  const date = typeof data.toDate === 'function' ? data.toDate() : new Date(data)
  if (Number.isNaN(date.getTime())) return '—'
  return format(date, 'dd/MM/yyyy')
}

const textoEfetivacaoLiquidaQtde = (bruto, cancelamentos) => {
  if (!cancelamentos) return null
  return `Bruto ${bruto || 0} (− ${cancelamentos} cancelamentos)`
}

const textoDescontoOrtodontia = (quantidadeCancelamentos, valorDesconto, valorUnitario = VALOR_EFETIVACAO_ORTODONTIA) => {
  if (!quantidadeCancelamentos || !valorDesconto || valorDesconto <= 0) return null
  return `Descontado ${quantidadeCancelamentos} × ${formatMoney(valorUnitario)} (− ${formatMoney(valorDesconto)})`
}

const textoValorEfetivacaoOrtodontia = (valorUnitario = VALOR_EFETIVACAO_ORTODONTIA) =>
  `${formatMoney(valorUnitario)} por contrato formalizado`

const estiloAlertaCancelamento = (percentual) => {
  const pct = Number(percentual) || 0
  if (pct > LIMITE_CANCELAMENTO) {
    return { borderColor: '#dc3545', background: '#fff5f5', color: '#842029' }
  }
  if (pct >= LIMITE_CANCELAMENTO) {
    return { borderColor: '#fd7e14', background: '#fff8f0', color: '#984c0c' }
  }
  return null
}

const HelpIcon = ({ id, texto }) => (
  <OverlayTrigger
    trigger={['hover', 'focus']}
    placement='top'
    overlay={<Tooltip id={`dashboard-help-${id}`}>{texto}</Tooltip>}
  >
    <span
      className='text-muted ml-1'
      style={{ cursor: 'help', fontSize: 11, lineHeight: 1, flexShrink: 0 }}
      tabIndex={0}
      aria-label='Ajuda'
    >
      <FontAwesomeIcon icon={faQuestionCircle} />
    </span>
  </OverlayTrigger>
)

const SummaryCard = ({ id, label, value, secondary, help, highlight = false, toneStyle }) => {
  const cardStyle = {
    minHeight: 124,
    ...(toneStyle
      ? { background: toneStyle.background, borderColor: toneStyle.borderColor }
      : { background: highlight ? '#f5f9ff' : '#fff' }),
  }

  return (
    <Card className={`h-100 border ${!toneStyle && highlight ? 'border-primary' : ''}`} style={cardStyle}>
      <Card.Body className='d-flex flex-column p-3'>
        <div
          className='text-muted text-uppercase d-flex align-items-start'
          style={{ fontSize: 11, letterSpacing: 0.3, lineHeight: 1.3, minHeight: 28 }}
        >
          <span className='flex-grow-1' style={{ color: toneStyle?.color }}>{label}</span>
          {help ? <HelpIcon id={id || label} texto={help} /> : null}
        </div>
        <div
          className={`font-weight-bold ${!toneStyle && highlight ? 'text-primary' : 'text-dark'}`}
          style={{ fontSize: 20, lineHeight: 1.25, color: toneStyle?.color, wordBreak: 'break-word' }}
        >
          {value}
        </div>
        <div
          className='mt-auto pt-2'
          style={{
            fontSize: 11,
            lineHeight: 1.35,
            minHeight: 36,
            color: toneStyle?.color || '#6c757d',
          }}
        >
          {secondary || '\u00A0'}
        </div>
      </Card.Body>
    </Card>
  )
}

const MetricCard = SummaryCard

const textoComissaoEntradaSeparado = (percentuais, podeVerComissao) => {
  if (!podeVerComissao) return null
  return `Faixas: até 200k ${formatPercent(percentuais.efetivacao)} · >250k ${formatPercent(
    percentuais.efetivacaoAcima250
  )} · >300k ${formatPercent(percentuais.efetivacaoAcima300)} · Entrada: ${formatPercent(
    percentuais.efetivacaoEntrada
  )}`
}

const textoFaixaEfetivacaoAplicada = (percentualAplicado, percentualEntrada) => {
  if (!percentualAplicado && percentualAplicado !== 0) return null
  return `Faixa aplicada: ${formatPercent(percentualAplicado)} · Entrada: ${formatPercent(percentualEntrada)}`
}

export default function DashboardColaborador() {
  const [isLoading, setIsLoading] = useState(false)
  const [filtroDe, setFiltroDe] = useState(startOfMonth(new Date()))
  const [filtroAte, setFiltroAte] = useState(new Date())
  const [dadosDashboard, setDadosDashboard] = useState([])
  const [podeFiltrarColaborador, setPodeFiltrarColaborador] = useState(false)
  const [podeVerComissao, setPodeVerComissao] = useState(false)
  const [colaborador, setColaborador] = useState(null)
  const [colaboradores, setColaboradores] = useState([])
  const [especialidade, setEspecialidade] = useState(null)
  const [especialidades, setEspecialidades] = useState([])
  const [filtrosProntos, setFiltrosProntos] = useState(false)
  const [percentuais, setPercentuais] = useState({
    producao: 0,
    efetivacao: 0,
    efetivacaoAcima250: 0,
    efetivacaoAcima300: 0,
    efetivacaoEntrada: 0,
  })
  const [totalizadores, setTotalizadores] = useState({
    totalContratosEfetivados: 0,
    totalContratosEfetivadosBruto: 0,
    totalProcedimentos: 0,
    totalValorEfetivacao: 0,
    totalValorEfetivacaoBruto: 0,
    totalProducao: 0,
    totalComissaoProducao: 0,
    totalComissaoEfetivacao: 0,
    totalComissao: 0,
    percentualDescontoComissaoCancelamento: 0,
    valorDescontoComissaoCancelamento: 0,
    totalValorEntradaRecebido: 0,
    percentualEntradaSobreEfetivacao: 0,
    totalContratosComEntrada: 0,
    totalCancelamentos: 0,
    totalValorCancelamento: 0,
    percentualCancelamentoSobreEfetivacao: 0,
    totalCancelamentosDescontadosEfetivacao: 0,
    valorUnitarioEfetivacaoOrtodontia: VALOR_EFETIVACAO_ORTODONTIA,
  })
  const [openItems, setOpenItems] = useState([])
  const [valorUnitarioOrtodontia, setValorUnitarioOrtodontia] = useState(VALOR_EFETIVACAO_ORTODONTIA)

  const user = JSON.parse(localStorage.getItem(SystemConstants.getUser()))

  useEffect(() => {
    accountService.hasPermission(user.franquiaId, '/dashboard/colaborador').then((result) => {
      if (!result.data) window.location.href = '/'
    })

    const carregarFiltros = async () => {
      try {
        const [{ data: listaColaboradores }, { data: listaEspecialidades }, { data: listaPerfis }] = await Promise.all([
          colaboradoresService.getAllColaboradores(),
          colaboradoresService.getAllEspecialidades(),
          perfilService.getPerfis(),
        ])

        setColaboradores(
          filtrarColaboradoresPorPerfisPermitidos(listaColaboradores, listaPerfis).map(({ nome, id }) => ({
            label: nome,
            value: id,
          }))
        )

        const optionsEspecialidade = (listaEspecialidades || []).map(({ nome, id }) => ({
          label: nome,
          value: id,
        }))
        setEspecialidades(optionsEspecialidade)

        const clinicoGeral = optionsEspecialidade.find((opcao) =>
          normalizarTexto(opcao.label).includes(normalizarTexto(ESPECIALIDADE_PADRAO))
        )
        setEspecialidade(clinicoGeral || optionsEspecialidade[0] || null)
      } catch (error) {
        console.error(error)
      } finally {
        setFiltrosProntos(true)
      }
    }

    carregarFiltros()
  }, [])

  useEffect(() => {
    if (!filtrosProntos || !especialidade?.value) return
    updateDashboard()
  }, [filtroDe, filtroAte, colaborador, especialidade, filtrosProntos])

  const updateDashboard = async () => {
    setIsLoading(true)
    try {
      const filtros = [
        { campo: 'dataInicial', valor: filtroDe },
        { campo: 'dataFinal', valor: filtroAte },
        { campo: 'especialidadeId', valor: especialidade.value },
      ]

      if (colaborador?.value) {
        filtros.push({ campo: 'colaboradorId', valor: colaborador.value })
      }

      const response = await dashboardService.getDashboardColaborador(filtros)
      setDadosDashboard(response.data || [])
      setPodeFiltrarColaborador(!!response.podeFiltrarColaborador)
      setPodeVerComissao(!!response.podeVerComissao)
      setPercentuais({
        producao: response.percentualProducao || 0,
        efetivacao: response.percentualEfetivacao || 0,
        efetivacaoAcima250: response.percentualEfetivacaoAcima250Mil || 0,
        efetivacaoAcima300: response.percentualEfetivacaoAcima300Mil || 0,
        efetivacaoEntrada: response.percentualEfetivacaoEntrada || 0,
      })
      setTotalizadores({
        totalContratosEfetivados: response.totalContratosEfetivados || 0,
        totalContratosEfetivadosBruto: response.totalContratosEfetivadosBruto || 0,
        totalProcedimentos: response.totalProcedimentos || 0,
        totalValorEfetivacao: response.totalValorEfetivacao || 0,
        totalValorEfetivacaoBruto: response.totalValorEfetivacaoBruto || 0,
        totalProducao: response.totalProducao || 0,
        totalComissaoProducao: response.totalComissaoProducao || 0,
        totalComissaoEfetivacao: response.totalComissaoEfetivacao || 0,
        totalComissao: response.totalComissao || 0,
        percentualDescontoComissaoCancelamento: response.percentualDescontoComissaoCancelamento || 0,
        valorDescontoComissaoCancelamento: response.valorDescontoComissaoCancelamento || 0,
        totalValorEntradaRecebido: response.totalValorEntradaRecebido || 0,
        percentualEntradaSobreEfetivacao: response.percentualEntradaSobreEfetivacao || 0,
        totalContratosComEntrada: response.totalContratosComEntrada || 0,
        totalCancelamentos: response.totalCancelamentos || 0,
        totalValorCancelamento: response.totalValorCancelamento || 0,
        percentualCancelamentoSobreEfetivacao: response.percentualCancelamentoSobreEfetivacao || 0,
        totalCancelamentosDescontadosEfetivacao: response.totalCancelamentosDescontadosEfetivacao || 0,
        valorUnitarioEfetivacaoOrtodontia:
          response.valorUnitarioEfetivacaoOrtodontia || VALOR_EFETIVACAO_ORTODONTIA,
      })
      setValorUnitarioOrtodontia(response.valorUnitarioEfetivacaoOrtodontia || VALOR_EFETIVACAO_ORTODONTIA)
    } catch (error) {
      console.error(error)
    }
    setIsLoading(false)
  }

  const toggleItem = (key) => {
    if (openItems.includes(key)) {
      setOpenItems(openItems.filter((item) => item !== key))
    } else {
      setOpenItems([...openItems, key])
    }
  }

  const ehOrtodontia = normalizarTexto(especialidade?.label).includes('ORTODONT')
  const ehClinico = !ehOrtodontia

  const descontoTotalTexto = ehOrtodontia
    ? textoDescontoOrtodontia(
        totalizadores.totalCancelamentosDescontadosEfetivacao,
        totalizadores.valorDescontoComissaoCancelamento,
        valorUnitarioOrtodontia
      )
    : null
  const estiloCancelamentoTotal = estiloAlertaCancelamento(totalizadores.percentualCancelamentoSobreEfetivacao)
  const textoEntradaComissao = ehClinico ? textoComissaoEntradaSeparado(percentuais, podeVerComissao) : null
  const textoEntradaTotal = [
    `${formatPercent(totalizadores.percentualEntradaSobreEfetivacao)} do valor de contratos formalizados`,
    textoEntradaComissao,
  ]
    .filter(Boolean)
    .join(' · ')
  const textoComissaoEfetivacao = ehOrtodontia
    ? textoValorEfetivacaoOrtodontia(valorUnitarioOrtodontia)
    : `até 200k ${formatPercent(percentuais.efetivacao)} · >250k ${formatPercent(
        percentuais.efetivacaoAcima250
      )} · >300k ${formatPercent(percentuais.efetivacaoAcima300)} · Entrada ${formatPercent(
        percentuais.efetivacaoEntrada
      )}`
  const textoValorEfetivacaoTotal = ehOrtodontia
    ? textoValorEfetivacaoOrtodontia(valorUnitarioOrtodontia)
    : null

  return (
    <Fragment>
      {isLoading || !filtrosProntos ? (
        <PageLoading />
      ) : (
        <Fragment>
          <div className='dashboard-colaborador-header'>
            <div className='d-flex align-items-start justify-content-between flex-wrap mb-3'>
              <h2 className='text-secondary font-weight-bold mb-0 mr-3'>
                Dashboard <small>Colaborador</small>
              </h2>
              {podeVerComissao && (
                <small className='text-muted mt-1'>
                  {ehOrtodontia
                    ? `Contratos formalizados: ${formatMoney(valorUnitarioOrtodontia)} por contrato`
                    : `Produção: ${percentuais.producao}% | Contratos formalizados: até 200k ${percentuais.efetivacao}% · >250k ${percentuais.efetivacaoAcima250}% · >300k ${percentuais.efetivacaoAcima300}% | Entrada: ${percentuais.efetivacaoEntrada}%`}
                </small>
              )}
            </div>

            <div className='dashboard-colaborador-header__filtros'>
              <Form.Group className='required' style={{ minWidth: 220 }}>
                <Form.Label>Especialidade</Form.Label>
                <Select
                  placeholder='Selecione'
                  options={especialidades}
                  onChange={setEspecialidade}
                  value={especialidade}
                  isClearable={false}
                  noOptionsMessage={() => 'Nenhuma especialidade'}
                  menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                  menuPosition='fixed'
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    menu: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                />
              </Form.Group>
              {podeFiltrarColaborador && (
                <Form.Group style={{ minWidth: 220 }}>
                  <Form.Label>Colaborador</Form.Label>
                  <Select
                    placeholder='Todos'
                    options={colaboradores}
                    onChange={setColaborador}
                    value={colaborador}
                    isClearable
                    noOptionsMessage={() => 'Nenhum colaborador'}
                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                    menuPosition='fixed'
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      menu: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                  />
                </Form.Group>
              )}
              <Form.Group className='required'>
                <Form.Label>De</Form.Label>
                <MuiPickersUtilsProvider utils={MomentUtils} locale='pt-br'>
                  <KeyboardDatePicker
                    invalidDateMessage='Formato de data inválido'
                    className='form-control'
                    variant='inline'
                    value={filtroDe}
                    onChange={setFiltroDe}
                    format='DD/MM/YYYY'
                    required
                    animateYearScrolling
                    disableFuture
                  />
                </MuiPickersUtilsProvider>
              </Form.Group>
              <Form.Group className='required'>
                <Form.Label>Até</Form.Label>
                <MuiPickersUtilsProvider utils={MomentUtils} locale='pt-br'>
                  <KeyboardDatePicker
                    invalidDateMessage='Formato de data inválido'
                    className='form-control'
                    value={filtroAte}
                    onChange={setFiltroAte}
                    variant='inline'
                    format='DD/MM/YYYY'
                    required
                    animateYearScrolling
                    disableFuture
                  />
                </MuiPickersUtilsProvider>
              </Form.Group>
            </div>
          </div>

          <div className='dashboard-colaborador-sticky' aria-label='Filtros aplicados'>
            <div className='dashboard-colaborador-sticky__resumo'>
              <div className='dashboard-colaborador-sticky__chip'>
                <span>Especialidade</span>
                <strong>{especialidade?.label || '—'}</strong>
              </div>
              {podeFiltrarColaborador && (
                <div className='dashboard-colaborador-sticky__chip'>
                  <span>Colaborador</span>
                  <strong>{colaborador?.label || 'Todos'}</strong>
                </div>
              )}
              <div className='dashboard-colaborador-sticky__chip'>
                <span>Período</span>
                <strong>
                  {formatFiltroData(filtroDe)} — {formatFiltroData(filtroAte)}
                </strong>
              </div>
            </div>
          </div>

          <Row className='mb-4'>
            <Col lg={3} md={4} sm={6} className='mb-3'>
              <SummaryCard
                id='contratosEfetivados'
                label='Contratos formalizados'
                value={totalizadores.totalContratosEfetivados}
                help={CARD_HELPS.contratosEfetivados}
              />
            </Col>
            <Col lg={3} md={4} sm={6} className='mb-3'>
              <SummaryCard
                id='valorEfetivacao'
                label='Valor de contratos formalizados'
                value={formatMoney(totalizadores.totalValorEfetivacao)}
                help={CARD_HELPS.valorEfetivacao}
                secondary={textoValorEfetivacaoTotal}
              />
            </Col>
            {ehClinico && (
              <>
                <Col lg={3} md={4} sm={6} className='mb-3'>
                  <SummaryCard
                    id='procedimentos'
                    label='Procedimentos'
                    value={totalizadores.totalProcedimentos}
                    help={CARD_HELPS.procedimentos}
                  />
                </Col>
                <Col lg={3} md={4} sm={6} className='mb-3'>
                  <SummaryCard
                    id='producao'
                    label='Produção'
                    value={formatMoney(totalizadores.totalProducao)}
                    help={CARD_HELPS.producao}
                  />
                </Col>
                <Col lg={3} md={4} sm={6} className='mb-3'>
                  <SummaryCard
                    id='contratosComEntrada'
                    label='Contratos com entrada'
                    value={totalizadores.totalContratosComEntrada}
                    help={CARD_HELPS.contratosComEntrada}
                  />
                </Col>
                <Col lg={3} md={4} sm={6} className='mb-3'>
                  <SummaryCard
                    id='valorEntrada'
                    label='Valor recebido de entrada'
                    value={formatMoney(totalizadores.totalValorEntradaRecebido)}
                    secondary={textoEntradaTotal}
                    help={CARD_HELPS.valorEntrada}
                  />
                </Col>
              </>
            )}
            {ehOrtodontia && (
              <>
                <Col lg={3} md={4} sm={6} className='mb-3'>
                  <SummaryCard
                    id='cancelamentos'
                    label='Cancelamentos no período'
                    value={totalizadores.totalCancelamentos}
                    help={CARD_HELPS.cancelamentos}
                  />
                </Col>
                <Col lg={3} md={4} sm={6} className='mb-3'>
                  <SummaryCard
                    id='valorCancelamento'
                    label='Valor de cancelamento'
                    value={formatMoney(totalizadores.totalValorCancelamento)}
                    secondary={`${formatPercent(totalizadores.percentualCancelamentoSobreEfetivacao)} do valor de contratos formalizados`}
                    help={CARD_HELPS.valorCancelamento}
                    toneStyle={estiloCancelamentoTotal}
                  />
                </Col>
              </>
            )}
            {podeVerComissao && (
              <>
                {ehClinico && (
                  <Col lg={3} md={4} sm={6} className='mb-3'>
                    <SummaryCard
                      id='comissaoProducao'
                      label='Produção (R$)'
                      value={formatMoney(totalizadores.totalComissaoProducao)}
                      secondary={`Calculado com ${formatPercent(percentuais.producao)}`}
                      help={CARD_HELPS.comissaoProducao}
                    />
                  </Col>
                )}
                <Col lg={3} md={4} sm={6} className='mb-3'>
                  <SummaryCard
                    id='comissaoEfetivacao'
                    label='Contratos formalizados (R$)'
                    value={formatMoney(totalizadores.totalComissaoEfetivacao)}
                    secondary={textoComissaoEfetivacao}
                    help={CARD_HELPS.comissaoEfetivacao}
                  />
                </Col>
                <Col lg={3} md={4} sm={6} className='mb-3'>
                  <SummaryCard
                    id='comissaoTotal'
                    label='Total (R$)'
                    value={formatMoney(totalizadores.totalComissao)}
                    secondary={descontoTotalTexto}
                    help={CARD_HELPS.comissaoTotal}
                    highlight
                  />
                </Col>
              </>
            )}
          </Row>

          {dadosDashboard.length > 0 && (
            <Accordion>
              {dadosDashboard.map((item, index) => (
                <Card key={item.colaboradorId || index} className={`colaborador-${index + 1}`}>
                  <Card.Header className='d-flex align-items-center justify-content-between flex-wrap'>
                    <Accordion.Toggle
                      as={Button}
                      variant='text'
                      onClick={() => toggleItem(index + 1)}
                      aria-controls={`collapse-${index + 1}`}
                      aria-expanded={openItems.includes(index + 1)}
                      className='text-left pl-0'
                    >
                      {item.nomeColaborador}
                    </Accordion.Toggle>
                    <div className='d-flex flex-wrap align-items-center'>
                      <Badge variant='secondary' className='mr-2 mb-1'>
                        {item.contratosEfetivados || 0} contratos formalizados
                      </Badge>
                      {ehClinico && (
                        <Badge variant='info' className='mr-2 mb-1'>
                          {item.totalProcedimentos || 0} procedimentos
                        </Badge>
                      )}
                      {podeVerComissao && (
                        <Badge variant='primary' className='mb-1'>
                          {formatMoney(item.totalComissao)}
                        </Badge>
                      )}
                    </div>
                  </Card.Header>
                  <Accordion.Collapse in={openItems.includes(index + 1)} id={`collapse-${index + 1}`}>
                    <Card.Body>
                      <Row className='mb-3'>
                        <Col lg={3} md={4} sm={6} className='mb-3'>
                          <MetricCard
                            id={`colab-${index}-contratos`}
                            label='Contratos formalizados'
                            value={item.contratosEfetivados || 0}
                            help={CARD_HELPS.contratosEfetivados}
                            secondary={
                              ehOrtodontia
                                ? textoEfetivacaoLiquidaQtde(
                                    item.contratosEfetivadosBruto,
                                    item.quantidadeCancelamentos
                                  )
                                : null
                            }
                          />
                        </Col>
                        <Col lg={3} md={4} sm={6} className='mb-3'>
                          <MetricCard
                            id={`colab-${index}-valor-efetivacao`}
                            label='Valor de contratos formalizados'
                            value={formatMoney(item.valorEfetivacao)}
                            help={CARD_HELPS.valorEfetivacao}
                            secondary={
                              ehOrtodontia ? textoValorEfetivacaoOrtodontia(valorUnitarioOrtodontia) : null
                            }
                          />
                        </Col>
                        {ehClinico && (
                          <>
                            <Col lg={3} md={4} sm={6} className='mb-3'>
                              <MetricCard
                                id={`colab-${index}-procedimentos`}
                                label='Procedimentos (produção)'
                                value={item.totalProcedimentos || 0}
                                help={CARD_HELPS.procedimentos}
                              />
                            </Col>
                            <Col lg={3} md={4} sm={6} className='mb-3'>
                              <MetricCard
                                id={`colab-${index}-producao`}
                                label='Produção'
                                value={formatMoney(item.totalProducao)}
                                help={CARD_HELPS.producao}
                              />
                            </Col>
                            <Col lg={3} md={4} sm={6} className='mb-3'>
                              <MetricCard
                                id={`colab-${index}-contratos-entrada`}
                                label='Contratos com entrada'
                                value={item.contratosComEntrada || 0}
                                help={CARD_HELPS.contratosComEntrada}
                              />
                            </Col>
                            <Col lg={3} md={4} sm={6} className='mb-3'>
                              <MetricCard
                                id={`colab-${index}-valor-entrada`}
                                label='Valor recebido de entrada'
                                value={formatMoney(item.valorEntradaRecebido)}
                                help={CARD_HELPS.valorEntrada}
                                secondary={[
                                  `${formatPercent(item.percentualEntradaSobreEfetivacao)} do valor de contratos formalizados`,
                                  textoEntradaComissao,
                                ]
                                  .filter(Boolean)
                                  .join(' · ')}
                              />
                            </Col>
                          </>
                        )}
                        {ehOrtodontia && (
                          <>
                            <Col lg={3} md={4} sm={6} className='mb-3'>
                              <MetricCard
                                id={`colab-${index}-cancelamentos`}
                                label='Cancelamentos no período'
                                value={item.quantidadeCancelamentos || 0}
                                help={CARD_HELPS.cancelamentos}
                              />
                            </Col>
                            <Col lg={3} md={4} sm={6} className='mb-3'>
                              <MetricCard
                                id={`colab-${index}-valor-cancelamento`}
                                label='Valor de cancelamento'
                                value={formatMoney(item.valorCancelamento)}
                                help={CARD_HELPS.valorCancelamento}
                                secondary={`${formatPercent(item.percentualCancelamentoSobreEfetivacao)} do valor de contratos formalizados`}
                                toneStyle={estiloAlertaCancelamento(item.percentualCancelamentoSobreEfetivacao)}
                              />
                            </Col>
                          </>
                        )}
                        {podeVerComissao && (
                          <>
                            {ehClinico && (
                              <Col lg={3} md={4} sm={6} className='mb-3'>
                                <MetricCard
                                  id={`colab-${index}-comissao-producao`}
                                  label='Produção (R$)'
                                  value={formatMoney(item.comissaoProducao)}
                                  help={CARD_HELPS.comissaoProducao}
                                  secondary={`Calculado com ${formatPercent(percentuais.producao)}`}
                                />
                              </Col>
                            )}
                            <Col lg={3} md={4} sm={6} className='mb-3'>
                              <MetricCard
                                id={`colab-${index}-comissao-efetivacao`}
                                label='Contratos formalizados (R$)'
                                value={formatMoney(item.comissaoEfetivacao)}
                                help={CARD_HELPS.comissaoEfetivacao}
                                secondary={
                                  ehOrtodontia
                                    ? textoComissaoEfetivacao
                                    : textoFaixaEfetivacaoAplicada(
                                        item.percentualEfetivacaoAplicado,
                                        percentuais.efetivacaoEntrada
                                      )
                                }
                              />
                            </Col>
                            <Col lg={3} md={4} sm={6} className='mb-3'>
                              <MetricCard
                                id={`colab-${index}-comissao-total`}
                                label='Total (R$)'
                                value={formatMoney(item.totalComissao)}
                                help={CARD_HELPS.comissaoTotal}
                                highlight
                                secondary={
                                  ehOrtodontia
                                    ? textoDescontoOrtodontia(
                                        item.cancelamentosDescontadosEfetivacao ||
                                          item.quantidadeCancelamentos,
                                        item.valorDescontoComissaoCancelamento,
                                        valorUnitarioOrtodontia
                                      )
                                    : null
                                }
                              />
                            </Col>
                          </>
                        )}
                      </Row>

                      {item.contratos?.length > 0 && (
                        <>
                          <h5 className='mt-3'>Contratos formalizados</h5>
                          <table className='table'>
                            <thead>
                              <tr>
                                <th>Número</th>
                                <th>Tipo</th>
                                <th>Paciente</th>
                                <th>Data</th>
                                <th>Valor</th>
                              </tr>
                            </thead>
                            <tbody>
                              {item.contratos.map((contrato) => (
                                <tr key={contrato.planoId}>
                                  <td>{contrato.numeroPlano}</td>
                                  <td>{contrato.tipoPlano}</td>
                                  <td className='text-uppercase'>{contrato.nomePaciente}</td>
                                  <td>{format(new Date(contrato.dataEfetivacao), 'dd/MM/yyyy')}</td>
                                  <td>{formatMoney(contrato.valorContrato)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </>
                      )}

                      {ehClinico && (
                        <Accordion>
                          {(item.producaoPorDia || []).map((dia, dataIndex) => (
                            <Card className='card-datas' key={`${index}-${dataIndex}`}>
                              <Card.Header>
                                <Accordion.Toggle
                                  as={Button}
                                  variant='text'
                                  onClick={() => toggleItem(`data-${index}-${dataIndex}`)}
                                  aria-expanded={openItems.includes(`data-${index}-${dataIndex}`)}
                                >
                                  <Badge variant='primary'>{dia.procedimentos?.length || 0}</Badge> {dia.data}
                                </Accordion.Toggle>
                              </Card.Header>
                              <Accordion.Collapse in={openItems.includes(`data-${index}-${dataIndex}`)}>
                                <Card.Body>
                                  <p>
                                    <b>Total de Procedimentos:</b> {dia.totalProcedimentos}
                                    <br />
                                    <b>Total Produção:</b> {formatMoney(dia.totalProducao)}
                                  </p>
                                  {(dia.procedimentos || []).map((procedimento, pIndex) => (
                                    <table className='table' key={pIndex}>
                                      <thead>
                                        <tr>
                                          <th>Nome</th>
                                          <th>CPF</th>
                                          <th>Número do Plano</th>
                                          <th>Procedimento</th>
                                          <th>Status</th>
                                          <th>Dente</th>
                                          <th>Valor</th>
                                          <th>Data Finalização</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        <tr>
                                          <td className='text-uppercase'>{procedimento.nome}</td>
                                          <td>{procedimento.cpf}</td>
                                          <td>{procedimento.numeroPlano}</td>
                                          <td>{procedimento.nomeProcedimento}</td>
                                          <td>
                                            {status.find((s) => s.value == procedimento.statusProcedimento)?.label}
                                          </td>
                                          <td>{procedimento.nomeDente}</td>
                                          <td>{formatMoney(procedimento.valor)}</td>
                                          <td>
                                            {procedimento.dataFinalizacao
                                              ? format(new Date(procedimento.dataFinalizacao), 'dd/MM/yyyy H:mm')
                                              : '-'}
                                          </td>
                                        </tr>
                                      </tbody>
                                    </table>
                                  ))}
                                </Card.Body>
                              </Accordion.Collapse>
                            </Card>
                          ))}
                        </Accordion>
                      )}
                    </Card.Body>
                  </Accordion.Collapse>
                </Card>
              ))}
            </Accordion>
          )}
        </Fragment>
      )}
    </Fragment>
  )
}
